import { Body, Button, Heading, IconButton } from '@assembly-js/design-system';
import { Skeleton } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { BillingIntervalToggle } from 'src/routes/settings/plans/components/studio/BillingIntervalToggle';
import { getStudioPlanTitle } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import {
  formatDollars,
  monthlyPriceCents,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type {
  SellableStudioPlanId,
  StudioBillingInterval,
} from 'src/routes/settings/plans/components/studio/studioPlanFeatures';

import {
  type AppLimitPlanOption,
  type AppLimitSurface,
  PAID_PLAN_INCLUDED_APPS,
} from './appLimit';
import { AppLimitModalShell } from './AppLimitModalShell';

interface AppLimitUpgradeModalProps {
  onClose: () => void;
  /** Submits the selected plan — for Free, into Stripe checkout. */
  onConfirm: () => void;
  /** The paid plans on offer, cheapest first. Empty while the catalog loads. */
  options: AppLimitPlanOption[];
  selectedPlan: SellableStudioPlanId;
  onSelectPlan: (plan: SellableStudioPlanId) => void;
  interval: StudioBillingInterval;
  onChangeInterval: (interval: StudioBillingInterval) => void;
  /**
   * Whether the upgrade is in flight. Stays open under a loading CTA, and locked
   * shut: the request either applies the plan or navigates to Stripe, and an
   * applied plan resumes the interrupted action — so dismissing mid-request
   * would start a build the user just walked away from.
   */
  isSubmitting?: boolean;
  /** The action being held back; decides the copy's verb. */
  surface: AppLimitSurface;
}

const MODAL_WIDTH_PX = 412;

/** Groups the radios so arrow keys move between plans rather than out of them. */
const PLAN_RADIO_GROUP = 'app-limit-upgrade-plan';

/** Placeholder rows while the catalog resolves, at the card's own height. */
const SKELETON_ROW_COUNT = 3;

const CARD_CLASS =
  'box-border flex h-10 cursor-pointer items-center gap-5 rounded border border-solid px-3 py-2';

const getTitle = (surface: AppLimitSurface): string =>
  surface === 'build'
    ? t(($) => $.page_apps.app_builder.app_limit_modal.upgrade.title.build)
    : t(($) => $.page_apps.app_builder.app_limit_modal.upgrade.title.add);

const getDescription = (surface: AppLimitSurface): string =>
  surface === 'build'
    ? t(
        ($) =>
          $.page_apps.app_builder.app_limit_modal.upgrade.description.build,
        {
          count: PAID_PLAN_INCLUDED_APPS,
        },
      )
    : t(
        ($) => $.page_apps.app_builder.app_limit_modal.upgrade.description.add,
        {
          count: PAID_PLAN_INCLUDED_APPS,
        },
      );

const PlanOption: React.FC<{
  option: AppLimitPlanOption;
  interval: StudioBillingInterval;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ option, interval, isSelected, onSelect }) => (
  // A label around the input, so the whole row is a hit target and the radio
  // stays the real control — keyboard navigation and grouping come for free.
  <label
    className={`${CARD_CLASS} ${
      isSelected
        ? 'border-black-heading bg-gray-100'
        : 'border-gray-150 bg-white'
    }`}
  >
    <input
      type="radio"
      name={PLAN_RADIO_GROUP}
      value={option.planId}
      checked={isSelected}
      onChange={onSelect}
      className="peer sr-only"
    />
    <span
      className={`box-border flex size-4 shrink-0 items-center justify-center rounded-full border border-solid peer-focus-visible:ring-1 peer-focus-visible:ring-[#0C41BB] ${
        isSelected ? 'border-black-heading' : 'border-gray-300'
      }`}
    >
      {isSelected && (
        <span className="size-2 rounded-full bg-black-heading" aria-hidden />
      )}
    </span>
    <Body
      tag="span"
      className="flex-1 truncate font-medium leading-[22px] text-black-heading"
    >
      {getStudioPlanTitle(option.planId)}
    </Body>
    <Body tag="span" size="sm" className="leading-[21px] text-gray-small">
      {t(($) => $.page_apps.app_builder.app_limit_modal.upgrade.price, {
        price: formatDollars(monthlyPriceCents(option.tier, interval)),
      })}
    </Body>
  </label>
);

/**
 * The Free paywall: adding another app is not something Free can buy, so the way
 * past it is a paid plan — which carries a larger allowance than Free's, so one
 * purchase settles it. The plans are picked here rather than on the Plans page
 * because the paywall interrupts an app the user has already shaped.
 */
export const AppLimitUpgradeModal: React.FC<AppLimitUpgradeModalProps> = ({
  onClose,
  onConfirm,
  options,
  selectedPlan,
  onSelectPlan,
  interval,
  onChangeInterval,
  isSubmitting = false,
  surface,
}) => {
  const title = getTitle(surface);
  const isCatalogPending = options.length === 0;
  // Nothing to submit unless the catalog actually carries the picked plan, so a
  // catalog that dropped it leaves a disabled CTA rather than a dead click.
  const isSubmittable = options.some(
    (option) => option.planId === selectedPlan,
  );

  return (
    <AppLimitModalShell
      ariaLabel={title}
      widthPx={MODAL_WIDTH_PX}
      isDismissDisabled={isSubmitting}
      onClose={onClose}
    >
      <div className="relative flex flex-col gap-8 bg-white px-8 pb-12 pt-8">
        <IconButton
          icon="Close"
          variant="minimal"
          size="sm"
          aria-label={t(($) => $.page_apps.app_builder.app_limit_modal.close)}
          onClick={onClose}
          disabled={isSubmitting}
          className="absolute right-2.5 top-5 rounded-full bg-gray-100 hover:bg-gray-150"
        />

        <div className="flex flex-col gap-1">
          <Heading
            size="xl"
            tag="h2"
            className="!mb-0 leading-[28px] text-black-heading"
          >
            {title}
          </Heading>
          <Body tag="div" className="leading-[22px] text-gray-small">
            {getDescription(surface)}
          </Body>
        </div>

        <div className="flex flex-col gap-4">
          <BillingIntervalToggle
            interval={interval}
            onChangeInterval={onChangeInterval}
          />
          <div className="flex flex-col gap-2">
            <Body
              tag="div"
              size="sm"
              className="leading-[21px] text-gray-small"
            >
              {t(
                ($) =>
                  $.page_apps.app_builder.app_limit_modal.upgrade.choose_plan,
              )}
            </Body>
            <div className="flex flex-col gap-3">
              {/* Sized like the real rows so the CTA doesn't jump under the
                  cursor when the catalog lands. */}
              {isCatalogPending
                ? Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                    <Skeleton key={index} variant="rounded" height={40} />
                  ))
                : options.map((option) => (
                    <PlanOption
                      key={option.planId}
                      option={option}
                      interval={interval}
                      isSelected={option.planId === selectedPlan}
                      onSelect={() => onSelectPlan(option.planId)}
                    />
                  ))}
            </div>
          </div>
        </div>

        <Button
          variant="primary"
          size="sm"
          label={t(($) => $.page_apps.app_builder.app_limit_modal.upgrade.cta)}
          className="box-border w-full"
          loading={isSubmitting}
          disabled={!isSubmittable || isSubmitting}
          onClick={onConfirm}
        />
      </div>
    </AppLimitModalShell>
  );
};
